// Reactive wiring between the room state + actions and the audio layer.
// Called from the Scenario screen via a useEffect on relevant state.

import { setAlarm, setPulseOx, defibCharge, defibDischarge, speak } from './audio';

const ARREST_RHYTHMS = new Set(['vf', 'vt_pulseless', 'asystole', 'pea_narrow', 'pea_broad', 'torsades']);

export function updateMonitorAudio(rhythm: string, hr: number, spo2: number, currentPhase: string): void {
  // Alarms
  if (rhythm === 'asystole') setAlarm('asystole');
  else if (rhythm === 'vf' || rhythm === 'vt_pulseless' || rhythm === 'torsades') setAlarm('vf');
  else if (rhythm === 'pea_narrow' || rhythm === 'pea_broad') setAlarm('pea');
  else if (spo2 > 0 && spo2 < 90 && currentPhase !== 'arrest') setAlarm('spo2_low');
  else setAlarm(null);

  // Pulse-ox beep — only when perfusing
  if (hr > 0 && spo2 > 0 && !ARREST_RHYTHMS.has(rhythm)) {
    setPulseOx(hr, spo2);
  } else {
    setPulseOx(0, 0);
  }
}

// Map action types → spoken closed-loop response from "team"
const ACTION_RESPONSES: Record<string, string[]> = {
  start_compressions: ['Compressions started. Counting.'],
  shock: ['Shock delivered. Resuming CPR.'],
  charge_defib: ['Charging. Stand clear of the patient.'],
  drug_admin: ['Drug pushed and flushed.'],
  insert_igel: ['I-gel in. Checking capno.'],
  insert_lma: ['LMA in. Cuff inflated.'],
  intubate_attempt: ['Tube passed. Confirm with capnography please.'],
  bvm_ventilation: ['Bagging at ten a minute.'],
  call_for_help: ['Arrest team activated. Help is on the way.'],
  declare_4hs_4ts: ['Noted: 4Hs and 4Ts reviewed.'],
  declare_differential: ['Working differential logged.'],
  rhythm_check: ['Briefly off the chest — what do you see?'],
  pulse_check_carotid: ['No pulse. Resume CPR.'],
  team_callout: ['Confirmed.'],
  ecg_12_lead: ['Twelve-lead acquired.'],
  abg: ['Sample sent to gas machine.'],
  sync_cardiovert: ['Synchronised shock delivered.'],
  pace_transcutaneous: ['Pacer running. Watching for capture.'],
  needle_thoracocentesis_l: ['Decompression — left side done.'],
  needle_thoracocentesis_r: ['Decompression — right side done.'],
  thrombolysis_alteplase: ['Alteplase running. Continuing CPR.'],
  kcl_emergency_bundle: ['Hyperkalaemia bundle in: calcium, insulin-dex, salbutamol.'],
};

export function announceAction(action_type: string, drug?: string, dose_mg?: number): void {
  if (action_type === 'drug_admin' && drug) {
    const display = dose_mg && dose_mg < 1 ? `${(dose_mg * 1000).toFixed(0)} micrograms` : `${dose_mg} milligrams`;
    speak(`${drug.replace('_', ' ')} ${display}, given.`, { voice: 'team_member' });
    return;
  }
  const phrases = ACTION_RESPONSES[action_type];
  if (!phrases) return;
  const phrase = phrases[Math.floor(Math.random() * phrases.length)] ?? phrases[0];
  speak(phrase!, { voice: 'team_member' });
}

export function announceEvent(kind: string): void {
  if (kind === 'rosc') speak('ROSC achieved. Post-resuscitation care.', { voice: 'team_leader' });
  if (kind === 'rhythm_change') speak('Rhythm changed. Reassess.', { voice: 'team_leader' });
}

export { defibCharge, defibDischarge };
